import { useEffect, useState } from "react";
import type { JSX } from "react";
import type { Recipe } from "../../shared/recipes";
import { matchRecipesRoute } from "../routing/usePathname";
import { CookMode } from "./CookMode";
import { RecipeDetail } from "./RecipeDetail";
import { RecipeForm } from "./RecipeForm";
import { RecipeImport } from "./RecipeImport";
import { RecipeList } from "./RecipeList";
import { fetchRecipe } from "../api/recipes";

type RecipesModuleProps = {
  pathname: string;
  onNavigate: (path: string) => void;
};

export function RecipesModule({ pathname, onNavigate }: RecipesModuleProps): JSX.Element {
  const route = matchRecipesRoute(pathname);

  useEffect(() => {
    if (route === undefined) {
      onNavigate("/recipes");
    }
  }, [route, onNavigate]);

  if (route === undefined) {
    return <div className="recipes-panel"><p className="muted-text">Loading…</p></div>;
  }

  if (route.view === "list") {
    return <RecipeList onNavigate={onNavigate} />;
  }

  if (route.view === "new") {
    return <RecipeForm onNavigate={onNavigate} />;
  }

  if (route.view === "import") {
    return <RecipeImport onNavigate={onNavigate} />;
  }

  if (route.view === "edit") {
    return <RecipeEditLoader recipeId={route.recipeId} onNavigate={onNavigate} />;
  }

  if (route.view === "detail") {
    return <RecipeDetail recipeId={route.recipeId} onNavigate={onNavigate} />;
  }

  return <CookMode recipeId={route.recipeId} sessionId={route.sessionId} onNavigate={onNavigate} />;
}

function RecipeEditLoader({ recipeId, onNavigate }: { recipeId: string; onNavigate: (path: string) => void }): JSX.Element {
  const [recipe, setRecipe] = useState<Recipe | undefined>(undefined);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    void fetchRecipe(recipeId)
      .then((loaded) => {
        if (active) {
          setRecipe(loaded);
        }
      })
      .catch(() => {
        if (active) {
          setError(true);
        }
      });
    return () => {
      active = false;
    };
  }, [recipeId]);

  if (error) {
    return (
      <div className="recipes-panel">
        <p className="error-text" role="alert">Recipe not found.</p>
        <button className="secondary-action" type="button" onClick={() => { onNavigate("/recipes"); }}>
          Back to library
        </button>
      </div>
    );
  }

  if (recipe === undefined) {
    return <div className="recipes-panel"><p className="muted-text">Loading recipe…</p></div>;
  }

  return <RecipeForm recipe={recipe} onNavigate={onNavigate} />;
}
